import React, { Component, PropTypes } from 'react';
import { connect } from 'react-redux';
import { uniqBy, get, find, head, isEmpty, size, some } from 'lodash';
import {
    SAVE_STORE_CHANGED,
    APP_LEAVING,
    SET_DEFAULT_HOTEL,
    SET_HOTELLIST,
    LOGIN_SET_THEME,
    ADD_THEME_LIST,
    ADD_CONFIG_LIST,
    LOGIN_SET_CONFIG,
    SET_LABELS,
    SET_HIGHER_ORDER_LABELS,
    SET_LANGUAGE_LIST,
    CHANGE_CUSTOM_CONTENT_LANGUAGE_CODE,
    LOGIN_SET_HOTELLIST,
    LOGIN_SET_CHAINID,
    LOGIN_SET_CHAINLIST,
    CALCULATE_FONT_USAGE,
    SELECT_SAVE_LEVEL,
    SET_RETAIL_PRODUCT_CATEGORIES,
    default as ActionTypes
} from '../../constants/ActionTypes';
import { HOTEL_LIST_MAX_LENGTH } from '../../constants/HotelConstants';
import UserPermissionLevels from '../../constants/UserPermissionLevels';
import CTModal from '../helpers/CTModal';
import Pagination from '../helpers/Pagination';
import { httpClient } from '../../../lib/utils';
import { envValue } from '../../../config/envUtils';
import { fetchLabels } from '../helpers/fetchLabels';
import { fetchLanguages } from '../helpers/fetchLanguages';
import { actions as LevelActions } from '../../store/LevelSelection';
import { SAVE_LEVEL, SAVE_LEVEL_LABELS } from '../../constants/Level';
import { ThemeService } from '../../services/ThemeService';
import { ConfigurationService, fetchRetailProductCategories } from '../../services/ConfigurationService';
import Message from '../message/Message';
import { reloadManageApps as reloadManageAppsAction } from '../../actions/thirdparty';
import './ModalProperty.scss';
import ConfirmDialog from './ConfirmDialog';

const FRONTMAN_URL = envValue('FRONTMAN_URL');
const NO_RESULTS = 'No hotels were found matching your search.';
const OVER_100_RESULTS = 'The search query returned over 100 results.';

class ModalProperty extends Component {
    constructor(props) {
        super(props);
        this.onClose = this.onClose.bind(this);
        this.handleSelectPropertyLevel = this.handleSelectPropertyLevel.bind(this);
        this.onChainSearchInputChange = this.onChainSearchInputChange.bind(this);
        this.onChainSearchButtonClick = this.onChainSearchButtonClick.bind(this);
        this.onAfterClose = this.onAfterClose.bind(this);
        this.selectHotel = this.selectHotel.bind(this);
        // this.state is currently used as we have no data from server!
        // Later the hotels are provided from the redux store -> props
        this.state = {
            levels: [SAVE_LEVEL.CHAIN, SAVE_LEVEL.HOTEL /* , SAVE_LEVEL.BRAND */],
            searchedHotelList: [],
            searchedChainList: [],
            chainSearchInput: '',
            chainErrorMessage: null,
            chainSearchButtonEnabled: false,
            hotelSearch: '',
            hotelErrorMessage: null,
            hotelSearchButtonEnabled: false,
            chainData: null
        };
        this.refSelectPropertyLevelElm = null;
        this.selectPropertyLevelElm = null;
    }

    canAccessChainLevel = () => !!this.props.userPermissionLevels.find(level => level === UserPermissionLevels.CHAIN);

    componentDidUpdate(prevProps) {
        if (this.props.selectedLevel !== prevProps.selectedLevel) {
            if (this.props.selectedLevel === SAVE_LEVEL.HOTEL) {
                this.setState({
                    hotelErrorMessage: null,
                    hotelSearch: '',
                    hotelSearchButtonEnabled: false
                });
                if (this.hotelInputRef) this.hotelInputRef.focus();
            } else if (this.props.selectedLevel === SAVE_LEVEL.CHAIN) {
                if (this.chainInputRef) this.chainInputRef.focus();
            }
        }
        if (this.props.open !== prevProps.open) {
            if (this.props.selectedLevel === SAVE_LEVEL.HOTEL && this.hotelInputRef) this.hotelInputRef.focus();
            else if (this.props.selectedLevel === SAVE_LEVEL.CHAIN && this.chainInputRef) this.chainInputRef.focus();
            const chainId = get(this.props, 'chain.Id');
            if (this.state.chainData === null && chainId) {
                httpClient
                    .get(
                        `${FRONTMAN_URL}/admin/hotels?view=Lookup&chainId=${chainId}&pageStart=0&pageSize=${HOTEL_LIST_MAX_LENGTH}`
                    )
                    .then(response => {
                        const chainData = get(head(get(response, 'data.hotels', [])), 'chainList.chain', []).find(
                            ch => ch.id.toString() === chainId.toString()
                        );
                        if (chainData) {
                            chainData.hotelsCount = get(response, 'data.pagination.total');
                            chainData.hotels =
                                chainData.hotelsCount <= HOTEL_LIST_MAX_LENGTH ? get(response, 'data.hotels', []) : [];
                            this.setState({ chainData });
                        }
                    });
            }
        }
    }

    onAfterClose() {
        this.setState({
            chainErrorMessage: null,
            searchedHotelList: [],
            searchedChainList: [],
            chainSearchInput: '',
            hotelSearch: '',
            hotelErrorMessage: null,
            hotelSearchButtonEnabled: false,
            chainSearchButtonEnabled: false
        });
        const { hotelList = [] } = this.props;
        const chainHotels = get(this.state.chainData, 'hotels', []);
        if (hotelList.length !== chainHotels.length) this.props.setHotellist(chainHotels);
    }

    onClose() {
        this.props.close();
        if (this.props.hotelId !== undefined || this.props.selectedLevel === SAVE_LEVEL.CHAIN) {
            return this.setState({ confirmModal: false });
        }
        const canAccessChainLevel = this.canAccessChainLevel();
        const body = canAccessChainLevel
            ? "By clicking 'Ok' you will proceed at the chain level.\nDo you want to continue?"
            : 'Please select hotel from the list or search for a hotel and then click select.';
        const header = canAccessChainLevel ? 'You did not choose a hotel.' : 'You have to select a hotel to proceed.';
        this.setState({ confirmModal: { header, body } });
    }

    onChainSearchInputChange(e) {
        const search = e.target.value;
        this.setState({ chainSearchInput: search, chainSearchButtonEnabled: /^[\d]+$/.test(search) });
    }

    onChainSearchButtonClick() {
        const chainId = this.state.chainSearchInput;
        const assignedChains = get(this.props, 'assignedChains.chains', []);
        const hasSystemAccess = get(this.props, 'assignedChains.systemAccess', false);

        if (!some(assignedChains, assignedChain => assignedChain.id === chainId) && !hasSystemAccess) {
            this.setState({ chainErrorMessage: 'You Don\'t Have Access to This Chain' });
            return;
        }

        const { toggleLoading } = this.props;

        toggleLoading(true);
        this.setState({ chainErrorMessage: null });

        this.getChainAndHotelData(chainId).then(data => {
            const hotels = get(data, 'hotels', []);
            if (hotels.length > 0) {
                const chainList = get(head(hotels), 'chainList.chain', []);
                const searchedChain = find(chainList, chain => chain.id === chainId);
                searchedChain.hotelsCount = get(data, 'pagination.total');
                this.setState({
                    searchedHotelList: hotels,
                    searchedChainList: [searchedChain]
                });
            } else {
                this.setState({ chainErrorMessage: 'Please Enter a Valid Chain Id' });
            }

            toggleLoading(false);
        });
    }

    getChainAndHotelData(chainId, pageStart = 0, perPage = HOTEL_LIST_MAX_LENGTH + 1) {
        return httpClient
            .get(
                `${FRONTMAN_URL}/admin/hotels?view=Lookup&${
                    chainId ? `chainId=${chainId}` : ''
                }&pageStart=${pageStart}&pageSize=${perPage}`
            )
            .then(response => (response.data ? response.data : {}))
            .catch(reason => {
                this.props.toggleLoading(false);
                this.setState({ chainErrorMessage: reason.toString() });
            });
    }

    selectHotel(selectedHotel) {
        if (!selectedHotel) return;
        this.props.setDefaultHotel(selectedHotel);
        if (this.props.selectedLevel === SAVE_LEVEL.HOTEL) {
            const hotelId = get(selectedHotel, 'id');
            const chainId = get(this.props.chain, 'Id');
            this.props.toggleLoading(true);
            Promise.all([
                new Promise(resolve => {
                    ConfigurationService.reload(
                        chainId,
                        hotelId,
                        /* TODO: table is being shown only on hotel level */ SAVE_LEVEL.HOTEL
                    ).then(() => resolve());
                }),
                new Promise(resolve => {
                    ThemeService.reload(
                        chainId,
                        hotelId,
                        /* TODO: table is being shown only on hotel level */ SAVE_LEVEL.HOTEL
                    ).then(() => resolve());
                }),
                new Promise(resolve => {
                    this.props.reloadManageApps(this.props.selectedLevel).then(() => resolve());
                }),
                fetchLanguages(
                    hotelId,
                    this.props.setLanguageList,
                    this.props.setCustomContentLanguageCode
                ).then((response) => {
                    const languageCode = get(find(response, { isDefault: true }), 'code') || 'en-US';
                    this.props.storeHasChanged();
                    fetchRetailProductCategories({ hotelId, languageCode })
                })
            ])
                .then(() => {
                    this.props.toggleLoading(false);
                })
                .catch(() => {
                    this.props.toggleLoading(false);
                });
        }
    }

    handleSelectPropertyLevel(e) {
        const selectedLevel = get(e, 'target.value');
        const isSelectedHotelExists = !isEmpty(this.props.selectedHotel);
        const tab = document.getElementById(selectedLevel);
        tab && tab.value === SAVE_LEVEL.HOTEL && !isSelectedHotelExists && tab.click();
        this.props.selectSaveLevel(selectedLevel, isSelectedHotelExists);
        selectedLevel === SAVE_LEVEL.CHAIN && this.props.setRetailProductCategories(null);
    }

    onHotelSearchChange = e => {
        const val = e.target.value;
        if (/^\d+$/.test(val)) this.setState({ hotelSearch: val, hotelSearchButtonEnabled: !!val.length });
        else this.setState({ hotelSearch: val, hotelSearchButtonEnabled: val.length > 2 });
    };

    onHotelSearchSubmit = e => {
        if (!this.state.hotelSearchButtonEnabled) return;
        let queryPart;
        const chainId = this.props.chain.Id;
        if (/^\d+$/.test(this.state.hotelSearch)) queryPart = 'hotelId=' + parseInt(this.state.hotelSearch);
        else queryPart = 'hotelName=' + encodeURIComponent(`%${this.state.hotelSearch}%`);
        this.props.toggleLoading(true);
        httpClient
            .get(`${FRONTMAN_URL}/admin/hotels?${chainId ? `chainId=${chainId}` : ''}&${queryPart}`)
            .then(response => {
                const hotelList = get(response, 'data.hotels', []);
                this.setState({
                    hotelErrorMessage: hotelList.length
                        ? get(response, 'data.pagination.total', 0) > 100
                            ? OVER_100_RESULTS
                            : null
                        : NO_RESULTS
                });
                this.props.setHotellist(hotelList);
                this.props.toggleLoading(false);
            })
            .catch(reason => {
                this.props.toggleLoading(false);
                this.setState({ hotelErrorMessage: reason.toString() });
            });
    };

    render() {
        const {
            hotelList = [],
            selectedLevel,
            assignedChains,
            chainId,
            chain,
            hotelId,
            hotelName,
            selectedHotel
        } = this.props;
        const { levels } = this.state;
        let dataToDisplay = uniqBy(hotelList, hotel => hotel.id).slice(0);
        const isSelectedHotelExists = !isEmpty(selectedHotel);
        const chainName = get(this.state.chainData, 'name', '');

        const accessLevels = levels.map((level, i) => {
            return (
                <label className="col-xs-4 col-sm-2 spark-toggle" key={`selectPopertyLevel_${i}`}>
                    <input
                        id={level}
                        name="chain-brand-hotel-level"
                        value={level}
                        checked={level === selectedLevel}
                        className="spark-toggle__input"
                        type="radio"
                    />
                    <span className="spark-label">{SAVE_LEVEL_LABELS[level]}</span>
                </label>
            );
        });
        const selectionLabel =
            selectedLevel === SAVE_LEVEL.CHAIN || (selectedLevel === SAVE_LEVEL.HOTEL && isSelectedHotelExists) ? (
                <Message
                    body={
                        <span>
                            <b>
                                {selectedLevel.substr(0, 1).toUpperCase() + selectedLevel.substr(1)}{' '}
                                {selectedLevel === SAVE_LEVEL.CHAIN ? chainId : hotelId}
                            </b>
                            {selectedLevel === SAVE_LEVEL.CHAIN ? ` ${chainName}` : `  ${hotelName}`}
                        </span>
                    }
                    heading={`Currently selected ${selectedLevel}`}
                    type="info"
                />
            ) : null;
        const canAccessChainLevel = this.canAccessChainLevel();
        const showTabs = canAccessChainLevel || size(get(assignedChains, 'chains', [])) > 1;
        return (
            <CTModal
                open={this.props.open}
                onClose={this.onClose}
                primaryButton
                primaryButtonClick={this.onClose}
                primaryButtonTitle="Close"
                onBeforeClose={this.onClose}
                onAfterClose={this.onAfterClose}
            >
                <div className="PropertyModal">
                    {isEmpty(this.state.confirmModal) ? null : (
                        <ConfirmDialog
                            body={this.state.confirmModal.body}
                            title={this.state.confirmModal.header}
                            onSecondaryAction={() => {
                                this.setState({ confirmModal: false });
                                //this.props.setLevel(SAVE_LEVEL.HOTEL);
                                this.props.reopen();
                            }}
                            closeFn={() => this.setState({ confirmModal: false })}
                            onPrimaryAction={() => {
                                this.props.setLevel(SAVE_LEVEL.CHAIN);
                                this.onAfterClose();
                            }}
                            primaryButtonTitle="Ok"
                            secondaryButtonTitle="Cancel"
                            hidePrimaryButton={!canAccessChainLevel}
                        />
                    )}
                    <h4 className="ModalHeading">
                        {selectedLevel === SAVE_LEVEL.CHAIN
                            ? 'Select a ' + SAVE_LEVEL_LABELS[this.props.selectedLevel]
                            : 'Select a Hotel in Chain ' + chainId}
                    </h4>
                    <div
                        onChange={this.handleSelectPropertyLevel}
                        ref={node => (this.refSelectPropertyLevelElm = node)}
                        className="row spark-toggle-group spark-margin-top"
                        style={{ marginBottom: 12 }}
                    >
                        {showTabs && levels.length > 1 && accessLevels}
                    </div>
                    {selectionLabel}
                    <h5>Search for a {this.props.selectedLevel}</h5>
                    <div className="propertyLevelTab">
                        {this.props.selectedLevel === SAVE_LEVEL.CHAIN ? (
                            <div className="flex-row">
                                <label className="spark-input">
                                    <input
                                        onChange={this.onChainSearchInputChange}
                                        className="spark-input__field"
                                        name="search-for-a-chain"
                                        key="Chain"
                                        role="textbox"
                                        value={this.state.chainSearchInput}
                                        onKeyUp={e =>
                                            e.keyCode === 13 && this.state.chainSearchButtonEnabled
                                                ? this.onChainSearchButtonClick()
                                                : null
                                        }
                                        ref={el => (this.chainInputRef = el)}
                                    />
                                    <span className="spark-label">
                                        What is the ID of the {SAVE_LEVEL_LABELS[this.props.selectedLevel]} you want to
                                        edit?
                                    </span>
                                    <div className="spark-input__addon">
                                        <i className="spark-icon-search spark-icon--md" />
                                    </div>
                                </label>
                                <button
                                    className="spark-btn spark-btn--md"
                                    onClick={this.onChainSearchButtonClick}
                                    disabled={!this.state.chainSearchButtonEnabled}
                                >
                                    Search
                                </button>
                                {this.state.chainErrorMessage && (
                                    <Message heading={this.state.chainErrorMessage} type="error" />
                                )}
                            </div>
                        ) : (
                            <div className="flex-row">
                                <label className="spark-input">
                                    <input
                                        onChange={this.onHotelSearchChange}
                                        className="spark-input__field"
                                        name="search-for-a-hotel"
                                        key="Hotel"
                                        role="textbox"
                                        value={this.state.hotelSearch}
                                        onKeyUp={e => (e.keyCode === 13 ? this.onHotelSearchSubmit() : null)}
                                        ref={el => (this.hotelInputRef = el)}
                                    />
                                    <span style={{ top: '8px' }} className="spark-label">
                                        What is the name or ID of the hotel you want to find?
                                    </span>
                                    <div className="spark-input__addon">
                                        <i className="spark-icon-search spark-icon--md" />
                                    </div>
                                </label>
                                <button
                                    className="spark-btn spark-btn--md"
                                    onClick={this.onHotelSearchSubmit}
                                    disabled={!this.state.hotelSearchButtonEnabled}
                                >
                                    Search
                                </button>
                                {this.state.hotelErrorMessage && (
                                    <Message
                                        heading={this.state.hotelErrorMessage}
                                        body={
                                            this.state.hotelErrorMessage === NO_RESULTS
                                                ? 'Please update the search criteria and try again.'
                                                : this.state.hotelErrorMessage === OVER_100_RESULTS
                                                ? 'Table will show only first 100 properties. Please revise your search to narrow it down.'
                                                : ''
                                        }
                                        type={
                                            this.state.hotelErrorMessage === NO_RESULTS ||
                                            this.state.hotelErrorMessage === OVER_100_RESULTS
                                                ? 'info'
                                                : 'error'
                                        }
                                    />
                                )}
                            </div>
                        )}
                        {this.props.selectedLevel === SAVE_LEVEL.HOTEL ? (
                            <PropertyTable
                                chain={this.props.chain}
                                fetchLabels={fetchLabels}
                                toggleLoading={isLoading => this.props.toggleLoading(isLoading)}
                                fetchLanguages={fetchLanguages}
                                setLanguageList={this.props.setLanguageList}
                                setLabels={this.props.setLabels}
                                setHigherOrderLabels={this.props.setHigherOrderLabels}
                                properties={dataToDisplay}
                                onSelectHotel={this.selectHotel}
                                setTheme={this.props.setTheme}
                                setThemeList={this.props.setThemeList}
                                setConfig={this.props.setConfig}
                                setConfigList={this.props.setConfigList}
                                selectedLevel={this.props.selectedLevel}
                                selectedHotelId={hotelId}
                            />
                        ) : (
                            <ChainTable
                                chainId={this.props.chainId}
                                reloadManageApps={this.props.reloadManageApps}
                                toggleLoading={isLoading => this.props.toggleLoading(isLoading)}
                                fetchLabels={fetchLabels}
                                fetchLanguages={fetchLanguages}
                                setLanguageList={this.props.setLanguageList}
                                setChain={chain => {
                                    this.props.setChain(chain.id, chain.name);
                                    this.setState({
                                        chainData: {
                                            ...chain,
                                            hotels:
                                                chain.hotelsCount <= HOTEL_LIST_MAX_LENGTH
                                                    ? this.state.searchedHotelList
                                                    : []
                                        }
                                    });
                                }}
                                setLabels={this.props.setLabels}
                                setHigherOrderLabels={this.props.setHigherOrderLabels}
                                properties={this.state.searchedChainList}
                                searchedHotelList={this.state.searchedHotelList}
                                onSelectHotel={this.selectHotel}
                                setTheme={this.props.setTheme}
                                setThemeList={this.props.setThemeList}
                                setConfig={this.props.setConfig}
                                setConfigList={this.props.setConfigList}
                                setHotellist={this.props.setHotellist}
                                setDefaultHotel={this.props.setDefaultHotel}
                                selectedLevel={this.props.selectedLevel}
                                refSelectPropertyLevelElm={this.refSelectPropertyLevelElm}
                                hotelList={this.props.hotelList}
                            />
                        )}
                    </div>
                </div>
            </CTModal>
        );
    }
}

ModalProperty.defaultProps = {
    open: false,
    selectedLevel: SAVE_LEVEL.HOTEL
};

ModalProperty.propTypes = {
    open: PropTypes.bool.isRequired,
    selectedLevel: PropTypes.string.isRequired,
    close: PropTypes.func.isRequired,
    hotelList: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            country: PropTypes.string,
            city: PropTypes.string.isRequired,
            id: PropTypes.any,
            selected: PropTypes.bool
        })
    ),
    setDefaultHotel: PropTypes.func.isRequired,
    selectSaveLevel: PropTypes.func.isRequired,
    storeHasChanged: PropTypes.func,
    setChain: PropTypes.func.isRequired,
    toggleLoading: PropTypes.func.isRequired,
    reloadManageApps: PropTypes.func.isRequired,
    userPermissionLevels: PropTypes.array,
    setRetailProductCategories: PropTypes.func
};

class ChainTable extends Component {
    constructor(props) {
        super(props);
        this.showPage = this.showPage.bind(this);
        this.state = {
            currentPage: 1,
            pageSize: 4
        };
    }

    showPage(pageNo) {
        this.setState({ currentPage: pageNo });
    }

    onSelectChainClick(item) {
        const {
            toggleLoading,
            reloadManageApps,
            setHotellist,
            setChain,
            onSelectHotel,
            searchedHotelList,
            setDefaultHotel
        } = this.props;
        toggleLoading(true);
        const hotelId = item.id;
        const chainId = item.id;
        Promise.all([
            new Promise(resolve => {
                ConfigurationService.reload(
                    chainId,
                    hotelId,
                    /* TODO: table is being shown only on hotel level */ SAVE_LEVEL.CHAIN
                ).then(() => resolve());
            }),
            new Promise(resolve => {
                ThemeService.reload(
                    chainId,
                    hotelId,
                    /* TODO: table is being shown only on hotel level */ SAVE_LEVEL.CHAIN
                ).then(() => resolve());
            }),
            new Promise(resolve => {
                reloadManageApps(SAVE_LEVEL.CHAIN, chainId, hotelId).then(() => resolve());
            })
        ]).then(() => {
            setChain(item);
            if (searchedHotelList.length <= HOTEL_LIST_MAX_LENGTH) {
                setHotellist(searchedHotelList);
                onSelectHotel(searchedHotelList[0]);
            } else {
                setHotellist([]);
                setDefaultHotel({});
            }
            toggleLoading(false);
        });
    }

    render() {
        const start = (this.state.currentPage - 1) * this.state.pageSize;
        const end = start + this.state.pageSize;
        const elements = _.filter(this.props.properties.slice(start, end), property => !!property);
        const totalElements = this.props.properties.length;
        return totalElements ? (
            <div className="spark-table">
                <div className="spark-table__scroll">
                    <table role="grid">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Chain Id</th>
                                <th className="spark-text-center">Select Chain</th>
                            </tr>
                        </thead>
                        <tbody>
                            {elements.map((item, i) => {
                                const isSelected = item.id === this.props.chainId;
                                return (
                                    <tr key={i}>
                                        <td>{item.name}</td>
                                        <td>{item.id}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button
                                                onClick={this.onSelectChainClick.bind(this, item)}
                                                className="spark-btn spark-btn--sm"
                                                disabled={isSelected}
                                            >
                                                {isSelected ? 'Selected' : 'Select'}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    onChange={this.showPage}
                    totalElements={totalElements}
                    currentPage={this.state.currentPage}
                    pageSize={this.state.pageSize}
                />
            </div>
        ) : null;
    }
}

class PropertyTable extends Component {
    constructor(props) {
        super(props);
        this.showPage = this.showPage.bind(this);
        this.state = {
            currentPage: 1,
            pageSize: 4,
            totalElements: props.properties.length
        };
    }

    showPage(pageNo) {
        this.setState({ currentPage: pageNo });
    }

    componentWillReceiveProps(nextProps) {
        if (this.state.currentPage > 1 && this.props.properties.length !== nextProps.properties.length) {
            this.setState({ currentPage: 1 });
        }
    }

    render() {
        const start = (this.state.currentPage - 1) * this.state.pageSize;
        const end = start + this.state.pageSize;
        const elements = this.props.properties.slice(start, end);
        const totalElements = this.props.properties.length;

        return totalElements ? (
            <div className="spark-table">
                <div className="spark-table__scroll">
                    <table role="grid">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Hotel Id</th>
                                {/* <th>Location</th> */}
                                <th className="spark-text-center" />
                            </tr>
                        </thead>
                        <tbody>
                            {elements.map(item => {
                                return (
                                    <tr key={item.id}>
                                        <td>{item.name}</td>
                                        <td>{item.id}</td>
                                        {/* <td>{item.city}</td> */}
                                        <td className="spark-table__actions spark-text-center">
                                            {item.id === this.props.selectedHotelId ? (
                                                <button className="spark-btn spark-btn--sm" disabled>
                                                    Selected
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={e => {
                                                        this.props.onSelectHotel(item);
                                                    }}
                                                    className="spark-btn spark-btn--sm"
                                                >
                                                    Select
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    onChange={this.showPage}
                    totalElements={totalElements}
                    currentPage={this.state.currentPage}
                    pageSize={this.state.pageSize}
                />
            </div>
        ) : null;
    }
}

PropertyTable.propTypes = {
    chain: PropTypes.object,
    selectedHotelId: PropTypes.number,
    properties: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            country: PropTypes.string,
            city: PropTypes.string.isRequired,
            id: PropTypes.any,
            selected: PropTypes.bool
        })
    ),
    onSelectHotel: PropTypes.func
};

/**
 STORE stuff. See store/AppStore.js
 */

export const SHOW_MODAL_PROPERTY = 'SHOW_MODAL_PROPERTY';
export const HIDE_MODAL_PROPERTY = 'HIDE_MODAL_PROPERTY';

const initialState = {
    isOpened: false
};

export const modalPropertyReducer = (state = initialState, action) => {
    switch (action.type) {
        case SHOW_MODAL_PROPERTY:
            return {
                ...state,
                isOpened: true
            };
        case HIDE_MODAL_PROPERTY:
        case APP_LEAVING:
            return {
                ...state,
                isOpened: false
            };
        default:
            return state;
    }
};

export const modalPropertyActions = {
    show() {
        return dispatch => {
            dispatch({ type: SHOW_MODAL_PROPERTY });
        };
    },
    hide() {
        return dispatch => {
            dispatch({ type: HIDE_MODAL_PROPERTY });
        };
    }
};

/**
 /STORE stuff. See store/AppStore.js
 */

const mapStateToProps = state => {
    return {
        selectedLevel: state.app.selected.level,
        open: state.app.modal.property.isOpened,
        hotelList: state.manage.hotelList,
        searchedHotelList: state.manage.searchedHotelList,
        searchedChainList: state.manage.searchedChainList,
        chain: state.app.selected.chain,
        reduxState: state,
        accessibleChains: state.manage.accessibleChains,
        chainId: state.app.selected.chain.Id,
        hotelId: state.app.selected.selectedHotel.id,
        selectedHotel: state.app.selected.selectedHotel,
        hotelName: state.app.selected.selectedHotel.name,
        assignedChains: state.oauth.assignedChains,
        userPermissionLevels: state.oauth.userPermissionLevels || []
    };
};

const mapDispatchToProps = dispatch => ({
    close: () => dispatch(modalPropertyActions.hide()),
    reopen: () => dispatch(modalPropertyActions.show()),
    storeHasChanged: () => dispatch({ type: SAVE_STORE_CHANGED }),
    setDefaultHotel: hotel => {
        dispatch({ type: SET_DEFAULT_HOTEL, payload: { hotel } });
    },
    setHotellist: hotelList => {
        dispatch({ type: SET_HOTELLIST, payload: { hotelList } });
    },
    setTheme: (theme, code) => {
        dispatch({ type: LOGIN_SET_THEME, theme, code });
        dispatch({ type: CALCULATE_FONT_USAGE });
    },
    setThemeList: (themeList, defaultContentCode) => {
        dispatch({ type: ADD_THEME_LIST, themeList, defaultContentCode });
    },
    setConfig: (theme, code) => {
        dispatch({ type: LOGIN_SET_CONFIG, config: theme, code });
    },
    setConfigList: (configList, defaultContentCode) => {
        dispatch({ type: ADD_CONFIG_LIST, configList, defaultContentCode });
    },
    setLabels: (foldedValue, explicitValue, languageCode) => {
        dispatch({ type: SET_LABELS, foldedValue, explicitValue, languageCode });
    },
    setHigherOrderLabels: foldedValue => {
        dispatch({ type: SET_HIGHER_ORDER_LABELS, foldedValue });
    },
    setChain: (chain, name) => {
        dispatch({ type: LOGIN_SET_CHAINID, chain, name });
    },
    setLanguageList: languageList => {
        dispatch({ type: SET_LANGUAGE_LIST, languageList });
    },
    setCustomContentLanguageCode: languageCode => {
        dispatch({ type: CHANGE_CUSTOM_CONTENT_LANGUAGE_CODE, languageCode });
    },
    setRetailProductCategories: (categories) => {
        dispatch({ type: SET_RETAIL_PRODUCT_CATEGORIES, categories });
    },
    setLevel: level => {
        dispatch(LevelActions.selectSaveLevel(level));
    },
    selectSaveLevel: (level, isSelectedHotelExists) => {
        if (level === SAVE_LEVEL.CHAIN) {
            dispatch(LevelActions.selectSaveLevel(level));
            dispatch(reloadManageAppsAction(level));
        }
        if (level === SAVE_LEVEL.HOTEL && isSelectedHotelExists) {
            dispatch(LevelActions.selectSaveLevel(level));
            dispatch(reloadManageAppsAction(level));
        } else {
            dispatch({ type: SELECT_SAVE_LEVEL, level });
        }
    },
    onReceiveHotelList: hotelList => {
        dispatch({ type: LOGIN_SET_HOTELLIST, hotelList });
    },
    onReceiveChainList: (chainList, accessibleChains) => {
        dispatch({ type: LOGIN_SET_CHAINLIST, chainList, accessibleChains });
    },
    reloadManageApps: (selectedLevel, hotelId, chainId) => {
        return dispatch(reloadManageAppsAction(selectedLevel, hotelId, chainId));
    }
});

export default connect(mapStateToProps, mapDispatchToProps)(ModalProperty);



// WEBPACK FOOTER //
// ./src/components/modals/ModalProperty.js